import { useEffect, useState } from "react";
import { Award, BarChart3, Trophy, TrendingDown } from "lucide-react";
import { useLeagueContext } from "@/modules/leagues/hooks/useLeagueContext";
import { useBonusQuestionsConfig, useSaveBonusQuestionsConfig } from "@/modules/leagues/hooks/useBonusQuestions";
import { SettingsSubHeader } from "@/modules/leagues/components/SettingsSubHeader";
import { Switch } from "@/components/atoms/Switch";
import { Input } from "@/components/atoms/Input";
import { Label } from "@/components/atoms/Label";
import { Button } from "@/components/atoms/Button";
import { Skeleton } from "@/components/atoms/Skeleton";
import { useToast } from "@/hooks/use-toast";
import { BONUS_QUESTION_CATALOG, BONUS_QUESTION_ORDER, type BonusQuestionSetting, type BonusQuestionType } from "@/modules/leagues/types/bonusQuestions";

const ICON_BY_TYPE: Record<BonusQuestionType, typeof Trophy> = {
  campeao: Trophy,
  vice_campeao: Trophy,
  terceiro_colocado: BarChart3,
  rebaixado: TrendingDown,
};

function toDatetimeLocalValue(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Perguntas Bônus (config do ADM) — 4 perguntas fixas sobre o DESFECHO do
 * campeonato (campeão/vice/3º/rebaixado), não mais perguntas livres por
 * rodada. O ADM escolhe quais ficam ativas, quantos pontos cada uma vale,
 * e define UM prazo único pra todas. A pontuação real só é aplicada depois
 * que o campeonato encerra de verdade (ver cron — pendente de conectar).
 */
export function LeagueSettingsBonusQuestionsPage() {
  const { league } = useLeagueContext();
  const { toast } = useToast();
  const { data: config, isLoading } = useBonusQuestionsConfig(league.id);
  const saveConfig = useSaveBonusQuestionsConfig(league.id);

  const [questions, setQuestions] = useState<Record<BonusQuestionType, BonusQuestionSetting>>(() =>
    Object.fromEntries(BONUS_QUESTION_ORDER.map((t) => [t, { active: false, pontos: 20 }])) as Record<
      BonusQuestionType,
      BonusQuestionSetting
    >
  );
  const [deadline, setDeadline] = useState<string>("");

  useEffect(() => {
    if (config) {
      setQuestions(
        Object.fromEntries(
          BONUS_QUESTION_ORDER.map((t) => [t, config.questions[t] ?? { active: false, pontos: 20 }])
        ) as Record<BonusQuestionType, BonusQuestionSetting>
      );
      setDeadline(toDatetimeLocalValue(config.deadline));
    }
  }, [config]);

  const activeCount = BONUS_QUESTION_ORDER.filter((t) => questions[t].active).length;
  const totalPontos = BONUS_QUESTION_ORDER.filter((t) => questions[t].active).reduce((sum, t) => sum + questions[t].pontos, 0);
  const canSave = activeCount === 0 || Boolean(deadline);

  function toggleQuestion(type: BonusQuestionType) {
    setQuestions((prev) => ({ ...prev, [type]: { ...prev[type], active: !prev[type].active } }));
  }

  function updatePontos(type: BonusQuestionType, pontos: number) {
    setQuestions((prev) => ({ ...prev, [type]: { ...prev[type], pontos: Math.max(0, pontos) } }));
  }

  function handleSave() {
    if (!canSave) {
      toast({ title: "Defina um prazo", description: "Com pelo menos uma pergunta ativa, o prazo de resposta é obrigatório.", variant: "destructive" });
      return;
    }
    saveConfig.mutate(
      { deadline: deadline ? new Date(deadline).toISOString() : null, questions },
      {
        onSuccess: () => toast({ title: "Perguntas Bônus salvas" }),
        onError: (err) =>
          toast({
            title: "Não foi possível salvar",
            description: err instanceof Error ? err.message : "Tente novamente.",
            variant: "destructive",
          }),
      }
    );
  }

  return (
    <div className="space-y-5 pb-40">
      <SettingsSubHeader leagueId={league.id} title="Perguntas Bônus" />
      <p className="-mt-3 text-sm text-muted-foreground">Selecione as perguntas e defina a pontuação para cada uma.</p>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 rounded-2xl border border-primary/40 bg-primary-50 px-4 py-3 dark:bg-primary-900/30">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
              <Award className="h-5 w-5" />
            </div>
            <div>
              <p className="font-display text-sm font-bold text-primary">
                {activeCount}/{BONUS_QUESTION_ORDER.length} ATIVAS
              </p>
              <p className="text-xs text-muted-foreground">+{totalPontos} pts totais possíveis</p>
            </div>
          </div>

          <div className="space-y-2">
            <h2 className="font-display text-sm font-bold">Prazo para respostas</h2>
            <input
              type="datetime-local"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              className="flex h-11 w-full rounded-xl border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <p className="text-[11px] text-muted-foreground">
              Depois desse prazo, ninguém mais consegue responder ou alterar a resposta.
            </p>
          </div>

          <div className="space-y-3">
            {BONUS_QUESTION_ORDER.map((type) => {
              const Icon = ICON_BY_TYPE[type];
              const q = questions[type];
              return (
                <div
                  key={type}
                  className={`rounded-2xl border p-4 transition-colors ${
                    q.active ? "border-primary/50 bg-primary-50/60 dark:bg-primary-900/20" : "border-border bg-card"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold">{BONUS_QUESTION_CATALOG[type].label}</p>
                      <p className="text-[11px] text-muted-foreground">{BONUS_QUESTION_CATALOG[type].helpText}</p>
                    </div>
                    <Switch checked={q.active} onCheckedChange={() => toggleQuestion(type)} aria-label={`Ativar ${BONUS_QUESTION_CATALOG[type].label}`} />
                  </div>
                  {q.active && (
                    <div className="mt-3 flex items-center gap-2 pl-12">
                      <Label htmlFor={`pontos-${type}`} className="shrink-0 text-xs">
                        Pontos ao acertar
                      </Label>
                      <Input
                        id={`pontos-${type}`}
                        type="number"
                        min={0}
                        value={q.pontos}
                        onChange={(e) => updatePontos(type, Number(e.target.value))}
                        className="h-9 w-24"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="fixed inset-x-0 bottom-[70px] z-30 border-t border-border bg-card/95 px-4 py-3 backdrop-blur-sm">
        <div className="container flex items-center justify-between">
          <span className="text-sm">
            <strong className="font-display text-primary">{activeCount}</strong> ativas
          </span>
          <span className="text-sm font-semibold text-primary">+{totalPontos} pts</span>
        </div>
        <div className="container mt-2">
          <Button className="w-full" onClick={handleSave} disabled={saveConfig.isPending}>
            {saveConfig.isPending ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
